import React, { useState, useRef, useEffect } from 'react';
import { 
  Activity, Pill, Coffee, Baby, CheckCircle2, 
  Heart, Plus, Trash2, Camera, X 
} from 'lucide-react';

// ✅ CONEXIÓN AL SISTEMA CENTRAL
import { useGlobal } from 'context/GlobalContext';

const CuidadosEspeciales = ({ ninos }) => {
  const { ninos: ninosGlobales } = useGlobal();
  const misNinos = ninos || ninosGlobales;

  const [ninoSeleccionado, setNinoSeleccionado] = useState(misNinos[0]?.id);
  const [cuidados, setCuidados] = useState({});

  useEffect(() => {
    if (misNinos.length > 0) {
      const inicial = {};
      misNinos.forEach(nino => {
        inicial[nino.id] = { 
          sensibilidades: nino.sensibilidades || [{ id: Date.now(), tipo: 'asusta', texto: '', foto: null }], 
          medicinas: nino.medicinas || [], 
          alergias: nino.alergias || '' 
        };
      });
      setCuidados(inicial);
    }
  }, [misNinos.length]);

  const cuidadoActual = cuidados[ninoSeleccionado] || { sensibilidades: [], medicinas: [], alergias: '' };

  // --- LÓGICA DE FOTOS (RESTAURADA) ---
  const manejarFoto = (id, seccion, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const editadas = cuidadoActual[seccion].map(item => 
          item.id === id ? { ...item, foto: reader.result } : item
        );
        setCuidados(prev => ({
          ...prev,
          [ninoSeleccionado]: { ...prev[ninoSeleccionado], [seccion]: editadas }
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const eliminarFoto = (id, seccion) => {
    const editadas = cuidadoActual[seccion].map(item => 
      item.id === id ? { ...item, foto: null } : item
    );
    setCuidados(prev => ({
      ...prev,
      [ninoSeleccionado]: { ...prev[ninoSeleccionado], [seccion]: editadas }
    }));
  };

  // --- ACCIONES DE MEDICAMENTOS Y SENSIBILIDAD ---
  const agregarMedicina = () => {
    const nueva = { id: Date.now(), nombre: '', dosis: '', hora: '', foto: null };
    setCuidados(prev => ({
      ...prev,
      [ninoSeleccionado]: { ...prev[ninoSeleccionado], medicinas: [...prev[ninoSeleccionado].medicinas, nueva] }
    }));
  };

  const actualizarMedicina = (id, campo, valor) => {
    const editadas = cuidadoActual.medicinas.map(m => m.id === id ? { ...m, [campo]: valor } : m);
    setCuidados(prev => ({
      ...prev,
      [ninoSeleccionado]: { ...prev[ninoSeleccionado], medicinas: editadas }
    }));
  };

  if (misNinos.length === 0) return <div className="p-20 text-center uppercase font-black opacity-30 italic">No hay exploradores registrados</div>;

  return (
    <div className="w-full max-w-[1400px] mx-auto space-y-10 animate-in fade-in duration-500 pb-20 px-4 font-sans">
      
      {/* HEADER Y SELECTOR */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 border-b-4 border-slate-100 pb-8 text-left">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 bg-rose-50 text-rose-600 px-4 py-2 rounded-full border border-rose-100 shadow-sm">
            <Heart size={16} fill="currentColor" />
            <span className="text-[10px] font-black uppercase tracking-widest italic">Protocolos Médicos</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 italic tracking-tighter leading-none uppercase">Bitácora de Salud</h2>
        </div>

        <div className="flex bg-white p-2 rounded-[2.5rem] shadow-xl border border-slate-100 gap-2 overflow-x-auto hide-scrollbar">
          {misNinos.map(nino => (
            <button
              key={nino.id}
              onClick={() => setNinoSeleccionado(nino.id)}
              className={`flex items-center gap-3 px-6 py-4 rounded-[1.8rem] transition-all shrink-0 ${
                ninoSeleccionado === nino.id ? 'bg-slate-900 text-white shadow-lg' : 'bg-slate-50 text-slate-400'
              }`}
            >
              <Baby size={16} />
              <span className="text-xs font-black uppercase tracking-widest">{nino.nombre.split(' ')[0]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        
        {/* SECCIÓN: SENSIBILIDAD */}
        <div className="bg-white p-8 rounded-[3.5rem] shadow-sm border border-slate-100 flex flex-col space-y-6 text-left">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-500 flex items-center justify-center">
                <Activity size={24} />
              </div>
              <h3 className="text-xl font-black text-slate-800 uppercase italic">Sensibilidad</h3>
            </div>
            <button onClick={() => {}} className="p-2 bg-indigo-600 text-white rounded-full"><Plus size={20} /></button>
          </div>

          <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 hide-scrollbar">
            {cuidadoActual.sensibilidades.map((s) => (
              <div key={s.id} className="p-4 bg-slate-50 rounded-[2rem] border border-slate-100 space-y-3 relative group">
                <select className="bg-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full text-indigo-600 shadow-sm outline-none">
                  <option>Le asusta</option>
                  <option>Le calma</option>
                  <option>Objeto Especial</option>
                </select>
                <textarea className="w-full bg-transparent border-none text-sm font-bold text-slate-700 outline-none resize-none" rows="2" placeholder="Nota de cuidado..." />
                
                <div className="relative">
                  {s.foto ? (
                    <div className="relative w-full h-32 rounded-2xl overflow-hidden border-2 border-white shadow-md">
                      <img src={s.foto} className="w-full h-full object-cover" alt="Nota visual" />
                      <button onClick={() => eliminarFoto(s.id, 'sensibilidades')} className="absolute top-2 right-2 bg-rose-500 text-white p-1 rounded-full"><X size={14} /></button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center w-full h-16 border-2 border-dashed border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-100 gap-2 text-slate-400">
                      <Camera size={18} />
                      <span className="text-[10px] font-bold uppercase">Foto de referencia</span>
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => manejarFoto(s.id, 'sensibilidades', e)} />
                    </label>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SECCIÓN: MEDICACIÓN */}
        <div className="bg-white p-8 rounded-[3.5rem] shadow-sm border border-slate-100 flex flex-col space-y-6 text-left">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center">
                <Pill size={24} />
              </div>
              <h3 className="text-xl font-black text-slate-800 uppercase italic">Medicación</h3>
            </div>
            <button onClick={agregarMedicina} className="p-2 bg-rose-600 text-white rounded-full"><Plus size={20} /></button>
          </div>

          <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 hide-scrollbar">
            {cuidadoActual.medicinas.map((m) => (
              <div key={m.id} className="p-5 bg-slate-50 rounded-[2.5rem] border border-slate-100 space-y-4 relative shadow-inner">
                <input type="text" placeholder="Nombre" value={m.nombre} onChange={(e) => actualizarMedicina(m.id, 'nombre', e.target.value)} className="w-full bg-white px-4 py-3 rounded-2xl text-sm font-black text-slate-800 outline-none shadow-sm" />
                <div className="grid grid-cols-2 gap-3">
                   <input type="text" placeholder="Dosis" value={m.dosis} onChange={(e) => actualizarMedicina(m.id, 'dosis', e.target.value)} className="w-full bg-white px-4 py-2 rounded-xl text-[10px] font-bold outline-none" />
                   <input type="time" value={m.hora} onChange={(e) => actualizarMedicina(m.id, 'hora', e.target.value)} className="w-full bg-white px-4 py-2 rounded-xl text-[10px] font-bold outline-none" />
                </div>
                <div className="relative">
                  {m.foto ? (
                    <div className="relative w-full h-32 rounded-2xl overflow-hidden border-2 border-white shadow-md">
                      <img src={m.foto} className="w-full h-full object-cover" alt="Frasco" />
                      <button onClick={() => eliminarFoto(m.id, 'medicinas')} className="absolute top-2 right-2 bg-rose-500 text-white p-1 rounded-full"><X size={14} /></button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center w-full h-20 border-2 border-dashed border-rose-200 rounded-2xl cursor-pointer hover:bg-rose-50 gap-2 text-rose-400">
                      <Camera size={20} />
                      <div className="text-left">
                        <p className="text-[10px] font-black uppercase leading-tight">Foto del Frasco</p>
                        <p className="text-[8px] font-bold opacity-60">Requerido</p>
                      </div>
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => manejarFoto(m.id, 'medicinas', e)} />
                    </label>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SECCIÓN: ALIMENTACIÓN */}
        <div className="bg-white p-8 rounded-[3.5rem] shadow-sm border border-slate-100 flex flex-col space-y-6 text-left">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-500 flex items-center justify-center">
              <Coffee size={24} />
            </div>
            <h3 className="text-xl font-black text-slate-800 uppercase italic">Alimentación</h3>
          </div>
          <textarea 
            value={cuidadoActual.alergias}
            onChange={(e) => setCuidados(prev => ({ ...prev, [ninoSeleccionado]: { ...prev[ninoSeleccionado], alergias: e.target.value }}))}
            className="w-full flex-1 p-8 bg-slate-50 rounded-[3rem] border-none text-sm font-bold text-slate-700 outline-none min-h-[400px] shadow-inner"
            placeholder="Alergias o restricciones alimentarias..."
          />
        </div>

      </div>

      {/* BOTÓN GUARDAR */}
      <div className="max-w-2xl mx-auto px-4">
        <button onClick={() => alert("Protocolos de salud actualizados")} className="w-full bg-slate-900 text-white p-8 rounded-[3rem] font-black text-lg uppercase tracking-[0.2em] shadow-2xl flex items-center justify-center gap-4 active:scale-95 transition-all border-b-[12px] border-slate-800">
          <CheckCircle2 size={28} className="text-emerald-400" />
          Guardar Protocolo de Seguridad
        </button>
      </div>
    </div>
  );
};

export default CuidadosEspeciales;